// NGRX
import { Observable } from 'rxjs';
import { RouterReducerState, routerReducer } from '@ngrx/router-store';
import { localStorageSync, LocalStorageConfig } from 'ngrx-store-localstorage';
import { ActionReducer, select, ActionReducerMap, MetaReducer } from '@ngrx/store';

import { createSelector } from 'reselect';
import * as olci from '../actions/olci';
import { constants } from '../constants';
import * as fromSession from './session';
import * as fromGuestDetails from './guest-details';
import * as fromGuests from './guests';
import * as fromSSPass from './setsailpass';
import * as fromContent from './content';
import * as fromArrival from './arrivaltimes';
import * as fromCruiseContract from './cruisecontract';
import * as fromOExpense from './o-expense';
import * as fromLayout from './layout';
import * as fromFeatureFlags from './feature-flags.reducer';
import * as fromAnalytics from './analytics.reducer';
import * as fromCruiseSurvey from './cruise-survey.reducer';
import * as fromCountries from './country.reducer';
import { Guest } from '../models';
import {addDays, dateFormat} from '../utils/date.utils';

// Third party libraries

// OLCI

/**
 * The compose function is one of our most handy tools. In basic terms, you give
 * it any number of functions and it returns a function. This new function
 * takes a value and chains it through every composed function, returning
 * the output.
 *
 * More: https://drboolean.gitbooks.io/mostly-adequate-guide/content/ch5.html
 */

/**
 * storeFreeze prevents state from being mutated. When mutation occurs, an
 * exception will be thrown. This is useful during development mode to
 * ensure that none of the reducers accidentally mutates the state.
 */

/**
 * combineReducers is another useful metareducer that takes a map of reducer
 * functions and creates a new reducer that stores the gathers the values
 * of each reducer and stores them using the reducer's key. Think of it
 * almost like a database, where every reducer is a table in the db.
 *
 * More: https://egghead.io/lessons/javascript-redux-implementing-combinereducers-from-scratch
 */

/**
 * Every reducer module's default export is the reducer function itself. In
 * addition, each module should export a type or interface that describes
 * the state of the reducer plus any selector functions. The `* as`
 * notation packages up all of the exports into a single object.
 */

/**
 * As mentioned, we treat each reducer like a table in a database. This means
 * our top level state interface is just a map of keys to inner state types.
 */
export interface AppState {
	guests: fromGuests.State;
	guestdetails: fromGuestDetails.State;
	setsailpass: fromSSPass.State;
	content: fromContent.State;
	router: RouterReducerState;
	session: fromSession.State;
	arrivaltimes: fromArrival.State;
	cruisecontract: fromCruiseContract.State;
	layout: fromLayout.State;
	oexpense: fromOExpense.State;
	reservationRetrievalResp: fromSession.State;
	featureFlags: fromFeatureFlags.FeatureFlagsState;
	analytics: fromAnalytics.State;
	cruiseSurvey: fromCruiseSurvey.State;
	countries: fromCountries.CountriesState;
}

/**
 * Because metareducers take a reducer function and return a new reducer,
 * we can use our compose helper to chain them together. Here we are
 * using combineReducers to make our top level reducer, and then
 * wrapping that in storeLogger. Remember that compose applies
 * the result from right to left.
 */

export const reducers: ActionReducerMap<AppState> = {
	guestdetails: fromGuestDetails.reducer,
	guests: fromGuests.reducer,
	content: fromContent.reducer,
	setsailpass: fromSSPass.reducer,
	router: routerReducer,
	session: fromSession.reducer,
	arrivaltimes: fromArrival.reducer,
	cruisecontract: fromCruiseContract.reducer,
	oexpense: fromOExpense.reducer,
	reservationRetrievalResp: fromSession.reducer,
	layout: fromLayout.reducer,
	featureFlags: fromFeatureFlags.reducer,
	analytics: fromAnalytics.reducer,
	cruiseSurvey: fromCruiseSurvey.reducer,
	countries: fromCountries.reducer
};

const localStorageSyncConfig: LocalStorageConfig = {
  keys: [
    'session',
    'guests',
    'content',
    'oexpense',
    'guestdetails',
    'cruisecontract',
    'featureFlags',
    'arrivaltimes',
    'analytics',
    'cruiseSurvey',
  ],
  storage: sessionStorage,
  rehydrate: true,
  removeOnUndefined: true,
};

export function localStorageSyncReducer(actionReducers: ActionReducer<AppState>): ActionReducer<AppState> {
	return localStorageSync(localStorageSyncConfig)(
		function (state: any, action: any) {
			switch (action.type) {
				case olci.OlciActionTypes.CLEAR_STATE_ONLY:
					return Object.assign({}, state, {
						guests: fromGuests.initialState,
						setsailpass: fromSSPass.initialState,
						session: fromSession.initialState,
						arrivaltimes: fromArrival.initialState,
						cruisecontract: fromCruiseContract.initialState,
						layout: fromLayout.initialState,
						guestdetails: fromGuestDetails.initialState,
						oexpense: fromOExpense.initialState,
						content: fromContent.initialState
					});

				case olci.OlciActionTypes.CLEAR_SESSION_ONLY:
					return Object.assign({}, state, {
						session: fromSession.initialState
					});
			}
			return actionReducers(state, action);
		}
	);
}

export const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];

/**
 * Every reducer module exports selector functions, however child reducers
 * have no knowledge of the overall state tree. To make them useable, we
 * need to make new selectors that wrap them.
 *
 * Once again our compose function comes in handy. From right to left, we
 * first select the books state then we pass the state to the book
 * reducer's getBooks selector, finally returning an observable
 * of search results.
 *
 * Share memoizes the selector functions and publishes the result. This means
 * every time you call the selector, you will get back the same result
 * observable. Each subscription to the resultant observable
 * is shared across all subscribers.
 */

/**
 * Guest Details Selectors
 */
export const guestDetailsState = (state: AppState) => state.guestdetails;
export const selectedGuestId = createSelector(guestDetailsState, fromGuestDetails.selectedGuestId);
export const selectedGuestDetails = createSelector(selectedGuestId, guestDetailsState, (gId: number, state: fromGuestDetails.State) => {
	if (!gId) {
		return null;
	}

	return state.guestDetails[gId];
});

export const selectedGuestPersonalInfo = createSelector(selectedGuestDetails, (s: any) => s && s.personalInfo);
export const selectedGuestHomeAddress = createSelector(selectedGuestDetails, (s: any) => s && s.homeAddress);
export const selectedGuestImmigrationInfo = createSelector(selectedGuestDetails, (s: any) => s && s.immigrationInfo);
export const selectedGuestContactInfo = createSelector(selectedGuestDetails, (s: any) => s && s.contactInfo);
export const selectedGuestEmergencyContactInfo = createSelector(selectedGuestDetails, (s: any) => s && s.emergencyContact);
export const selectedGuestTravelDocsList = createSelector(selectedGuestDetails, (s: any) => s && s.travelDocsList);
export const guestDetailsIsLoading = createSelector(guestDetailsState, fromGuestDetails.loading);

/**
 * The rest of the selctors, for now. Until we orrganize it
 */
export const getOexpenseState = (state: AppState) => state.oexpense;
export const getGuestsExpensesInformation = createSelector(getOexpenseState, fromOExpense.getGuestsExpensesInformation);
export const getGuestsExpensesError = createSelector(getOexpenseState, fromOExpense.getGuestsExpensesError);
export const getGuestsExpensesIsLoading = createSelector(getOexpenseState, fromOExpense.getGuestsExpensesIsLoading);
export const getDisplayCCForm = createSelector(getOexpenseState, fromOExpense.getDisplayCCForm);
export const getDisplayCCList = createSelector(getOexpenseState, fromOExpense.getDisplayCCList);
export const getSelectedCard = createSelector(getOexpenseState, fromOExpense.getSelectedCard);

/**
 * A selector function is a map function factory. We pass it parameters and it
 * returns a function that maps from the larger state tree into a smaller
 * piece of state. This selector simply selects the `books` state.
 *
 * Selectors are used with the `let` operator. They take an input observable
 * and return a new observable. Here's how you would use this selector:
 *
 * ```ts
 * class MyComponent {
 *  constructor(state$: Observable<fromRoot.State>) {
 *    this.booksState$ = state$.let(getBooksState());
 *  }
 * }
 * ```
 */

export const getContentState = (state: AppState) => state.content;
export const getRouterStateUrl = (state : AppState)=> state.router.state.url;
export const getContentRetrieval = createSelector(getContentState, fromContent.getContentRetrieval);
export const getLanguageCode = createSelector(getContentState, fromContent.getLanguageCode);
export const getSiteResourceBundle = createSelector(getContentRetrieval, (content: any) => {
	if (content !== null) {
		return content.resourceBundle;
	}

	return null;
});
export const getGovernanceContent = createSelector(getContentState, fromContent.getGovernanceContent);
export const getGovernanceContentCheckin = createSelector(getGovernanceContent, (governanceContent: any) =>
	governanceContent ? governanceContent.checkInEntryInterrupt : null
);
export const getCheckinEntryInterrupt = createSelector(getGovernanceContentCheckin, (governanceContent: any) =>
	governanceContent ? governanceContent.interrupt : null
);

export const getHealthCenterProtocols = createSelector(getContentState, fromContent.getHealthCenterProtocols)
export const getCheckInGuestSelectBanner = createSelector(getGovernanceContent,(governanceContent: any)=> governanceContent ? governanceContent.checkInGuestSelectBanner.interrupt : null )
export const getBoardingPassEntryInterrupt = createSelector(getGovernanceContent, (governanceContent: any) =>
	governanceContent ? governanceContent.boardingPassEntryInterrupt.interrupt : null
);
export const getVaccinationReminderInterrupt = createSelector(getGovernanceContent, (governanceContent: any) =>
	governanceContent && governanceContent.boardingPassWebVaccinationReminder
		? governanceContent.boardingPassWebVaccinationReminder.interrupt
		: null
);
export const isGuestUsingMobileDevice = createSelector(getContentRetrieval, (content: any) => {
	return content.mobileDevice;
});
export const getLanguageList = createSelector(getContentRetrieval, (content: any) => {
	if (content) {
		const languageMap = content.languageCodeIso;
		if (languageMap) {
			const arraylang = languageMap.split(',');
			return (arraylang.map((item: any) => {
				const itemsplit = item.split(':'); // 'en_Us:English:ENG'
				return {
					code: itemsplit[0],
					name: itemsplit[1],
					isocode: itemsplit[2]
				};
			}));
		}
	}
});
export const getCurrentlanguage = createSelector(getContentRetrieval, getLanguageList, (content: any, list: any) => {
	const defaultLanguage = {
		code: 'en_Us',
		name: 'English',
		isocode: 'ENG'
	};
	if (content !== null && content.languageCodeIso !== '') {
		if (list && list.length > 0) {
			if (content.languageCode !== null) {
				return list.find((item: any) => item.isocode === content.languageCode);
			} else {
				return list[0];
			}
		} else {
			return defaultLanguage;
		}
	} else {
		return defaultLanguage;
	}
});

export const getBrandCode = createSelector(getContentRetrieval, (content: any) => {
	if (content !== null) {
		return content.brandCode;
	}
});
export const getLayoutState = (state: AppState) => state.layout;
export const getLayoutOpenedModalName = createSelector(getLayoutState, fromLayout.getOpenedModalName);
export const getExitUrl = createSelector(getLayoutState, fromLayout.getExitUrl);
export const getLogoutFromGA = createSelector(getLayoutState, fromLayout.getLogoutFromGA);
export const isLoading = createSelector(getLayoutState, fromLayout.isLoading);
export const getGuestState = (state: AppState) => state.guests;
export const getEntitySelectedGuestId = createSelector(getGuestState, fromGuests.getSelectedGuestId);
export const getEntitySGuests = createSelector(getGuestState, fromGuests.getGuests);
export const getEntitySelectedGuest = createSelector(
	getEntitySGuests,
	getEntitySelectedGuestId,
	// tslint:disable-next-line: no-shadowed-variable
	(guests: any, selectedGuest: any) => {
		return guests.find((guest: any) => guest.guestId === selectedGuest);
	}
);
export const getEntitySelectedOther = createSelector(getGuestState, fromGuests.getSelectedOtherGuest);
export const progressIsLoaded = createSelector(getGuestState, fromGuests.getProgressIsLoaded);
export const getPhotoLoading = createSelector(getGuestState, fromGuests.loadingPhoto);
export const getGuestSelectedOtherInfo = createSelector(getEntitySGuests, getEntitySelectedOther, (guests: any, selectedOther: any) => {
	const selectedOId = parseInt(selectedOther.guestId, 10);
	return guests.find((guest: any) => guest.guestId === selectedOId);
});
export const getGuestSelected = createSelector(getGuestState, fromGuests.getSelectedGuest);
export const guestStateError = createSelector(getGuestState, fromGuests.getError);

export const getid = createSelector(getGuestState, getEntitySelectedGuestId, (selectedId: any) => {
	return selectedId;
});

export const getVdsId = (state: AppState) => (state.session.vds ? state.session.vds.vdsId : '');

export const getSelectedGuestPhoto = createSelector(getEntitySGuests, selectedGuestId, (listOfGuests: any, guestId: any) => {
	const selectedGuest = listOfGuests.find((g: any) => g.guestId === guestId);
	return selectedGuest && selectedGuest.base64Image ? selectedGuest.base64Image : '';
});

export const getSessionState = (state: AppState) => {
	return state.session;
}

export const getResponseErrors = createSelector(getSessionState,
	(sessionState: any) => {
		return sessionState.responseErrors;
	}
);

export const getFeatureFlagsState = (state: AppState) => state.featureFlags;

export const getAnalyticsState = (state: AppState) => state.analytics;

export const getSessionVar = createSelector(getSessionState, fromSession.getReservationRetrievalResp);
export const getSessionTimestamp = createSelector(
	getSessionState,
	(sessionState: any) => {
		if (sessionState !== undefined) {
			return sessionState.sessionTimestamp;
		} else {
			return 0;
		}
	}
);

export const getLoggedInGuestGA = createSelector(getSessionState, (sessionState: any) => {
	return sessionState.vds;
});

export const getSessionWarningModal = createSelector(getSessionState, fromSession.getSessionWarningModal);

export const getChekinToken = createSelector(getSessionState, fromSession.getCheckinToken);

export const getSessionBookingId = createSelector(getEntitySelectedGuest, getEntitySelectedOther, (guest: any, otherGuest: any) => {
	if (otherGuest !== null) {
		return {
			guestId: otherGuest.guestId,
			bookingId: otherGuest.bookingId
		};
	} else if (guest !== null) {
		return {
			guestId: guest.guestId,
			bookingId: guest.bookingId
		};
	}
});

export const getHealthCenterSailingInfo = createSelector(getSessionVar, (retrievalResponse: any) => {
  const sailDateStart = new Date(`${retrievalResponse.departureDate}T00:00:00`);
  const sailDateEnd = addDays(new Date(`${retrievalResponse.departureDate}T00:00:00`), retrievalResponse.sailingDays);
	return !retrievalResponse
    ? null :
    {
      shipName: retrievalResponse.shipName,
      sailDateStart,
      sailDateEnd,
      dateFormat: dateFormat(sailDateStart, sailDateEnd),
    };
});

/**
 * See bug OCLI-4704 on JIRA.
 */
export const getCompletionPercentage = createSelector(getSessionVar, (sessionVars: any) => {
	if (!sessionVars) {
		// This happens when no information from storage is still loaded. 0% of advance.
		return 0;
	}

	const smartShipFactor = sessionVars.enhancedSSP && sessionVars.arrivalTimeEnabled ? 5 : 0;
	return 90 + smartShipFactor;
});

export const getSmartship = createSelector(getSessionVar, (reservationRetrievalResp: any) => {
	if (!reservationRetrievalResp) {
		return {};
	}
	return reservationRetrievalResp.enhancedSSP;
});

/**
 * Given advance state of current user, this method determines if guest list is completed
 * or not. Consider the following:
 * - There are 5 moments on guest checkin, each one summing a percentage element:
 *   1. Guest details: Always sums 50%
 *   2. Onboard expenses: Always sums 20%
 *   3. Cruise detail contract: Sums 20% if it is a national guests, 0% for international
 *      guests as those does not sign a contract.
 *   4. Arrival times: sums 5% if in a smartship, 0% otherwise
 *   5. Boarding pass: sums 5% if in a smartship, 10% otherwise
 * - This method works with isGuestDetailsCompleted, isOnboardExpensesCompleted,
 *   isCruiseDetailsCompleted and getCompletionPercentage variables.
 */
const isGuestDetailsRequiredFormsCompleted = (guestParam: Guest) => {
	return (
		guestParam.moduleSpecificValue.personalDetails &&
		guestParam.moduleSpecificValue.addressContact &&
		guestParam.moduleSpecificValue.immigrationInfo &&
		guestParam.moduleSpecificValue.emergencyContact
	);
};

const getGalapagosSailing = createSelector(getSessionVar, (sailingInfo: any) => {
	if (sailingInfo !== null) {
		return sailingInfo.galapagosSailing;
	}
});

export const getGuestListCompleted = createSelector(
	getEntitySGuests,
	getSmartship,
	getGalapagosSailing,
	(guest: any, isSmartship: any, isGalapagosSailing: any) => {
		return guest
			? guest.filter(
				(guestParam: any) =>
					guestParam.olciStatus === constants.OLCI_STATUS.COMPLETED ||
					guestParam.olciStatus === constants.OLCI_STATUS.REPRINT ||
					// tslint:disable-next-line: max-line-length
					// Validations below are a workaround since microservice is not returning correct olci status for MIA SMART/INT NON SMART/INT SMART and GALAPAGOS SAILING
					(!isSmartship &&
						isGuestDetailsRequiredFormsCompleted(guestParam) &&
						guestParam.moduleSpecificValue.expenseAccount &&
						guestParam.moduleSpecificValue.cruiseContract) ||
					(isSmartship &&
						isGuestDetailsRequiredFormsCompleted(guestParam) &&
						guestParam.moduleSpecificValue.expenseAccount &&
						guestParam.moduleSpecificValue.cruiseContract &&
						guestParam.moduleSpecificValue.arrivalTimes) ||
					(isGalapagosSailing && !isSmartship && isGuestDetailsRequiredFormsCompleted(guestParam)) ||
					(isGalapagosSailing &&
						isSmartship &&
						isGuestDetailsRequiredFormsCompleted(guestParam) &&
						guestParam.moduleSpecificValue.expenseAccount &&
						guestParam.moduleSpecificValue.cruiseContract)
			)
			: null;
	}
);

export const getGuestsCompletedAndSelectedForCheckin = createSelector(getGuestListCompleted, (guestsCompleted: any) => {
	return guestsCompleted.filter((guest: any) => guest.selectedForCheckin === true);
});

export const getGuestListNotCompleted = createSelector(getEntitySGuests, getGuestListCompleted, (guests: any, guestsCompleted: any) => {
	return guests.filter((e: any) => -1 === guestsCompleted.indexOf(e));
});

export const getSelectdGuestsForCheckinProcess = createSelector(getEntitySGuests, (listOfGuests: any) => {
	let newOrderedGuestList = [];

	newOrderedGuestList = listOfGuests.filter((g: any) => g.selectedForCheckin === true);

	return newOrderedGuestList;
});

// Payload to Save naturalization
export const getImmigrationInfoNaturalizationToSave = createSelector(getEntitySelectedGuest, (guest: any) => {
	return {
		guestId: guest.guestId,
		immigrationInfo: {
			documentType: 'C', // Naturalization type
			documentNumber: '',
			documentExpirationDate: '',
			issuingCity: '',
			issueDate: '',
			issuingCountry: null,
			documentIssuingState: '',
			documentIssuingDate: '',
			photoDocumentType: '',
			driverLicenseState: '',
			visaNumber: '',
			visaIssueDate: '',
			visaExpiryDate: '',
			visaIssueCity: '',
			passportIssueCountry: '',
			documentNationality: '',
			dd: 'DD',
			mm: 'MONTH',
			yyyy: 'YYYY',
			companionID: 0
		}
	};
});

export const getCruiseContractState = (state: AppState) => state.cruisecontract;
export const getCruiseSessionState = (state: AppState) => state.session;
export const getContractsContent = createSelector(getCruiseContractState, fromCruiseContract.getContractsContent);
export const getTravelQuestionnaire = createSelector(getCruiseContractState, fromCruiseContract.getTravelQuestionnaire);
export const getTravelQuestionnaireForSelectedGuest = createSelector(getTravelQuestionnaire, selectedGuestId, (tq: any, guestId: any) => {
	const q = tq.find((questionaire: any) => questionaire.guestId === guestId);
	return { hq_one: q.hq1 }; //, hq_two: q.hq2
});

// tslint:disable-next-line: max-line-length
export const getGranteeConsentsInfo = createSelector(getCruiseContractState, fromCruiseContract.getGranteeConsentsInfo);
export const getGrantees = createSelector(getCruiseContractState, fromCruiseContract.getGrantees);
export const getgranteeConsentsList = createSelector(getCruiseContractState, fromCruiseContract.getgranteeConsentsList);
export const getGrantorInfo = createSelector(getCruiseContractState, fromCruiseContract.getGrantorInfo);
export const getErrorsFromCruiseContract = createSelector(getCruiseContractState, fromCruiseContract.getErrors);

export const isInternationalCruiseContract = createSelector(getEntitySGuests, (guestsList: any) => {
	const miamiOffice = guestsList.some((g: any) => g.bookingOfficeCode === 'MIA');
	return !miamiOffice;
});
export const getCruiseContractGrantorInfo = createSelector(
	getGranteeConsentsInfo,
	getEntitySelectedGuestId,
	// tslint:disable-next-line: no-shadowed-variable
	(granteeConsentsInfo: any, selectedGuest) => {
		return granteeConsentsInfo.filter(
			(item: any) => item.granteeInfo.guestId === selectedGuest && item.documentType === constants.CONSENTS_DOCUMENTS_TYPE.CTC
		);
	}
);

export const getContentCountryList = createSelector(getContentState, fromContent.getCountryList);
export const getContentResourceList = createSelector(getContentState, fromContent.getStaticContent); // this is content.resourceList.
export const getContentScreenSize = createSelector(getContentState, fromContent.getScreenSize);
export const getImmigrationDataVisible = createSelector(getContentState, fromContent.getDataVisibilityImmigration);

export const getArrivalState = (state: AppState) => state.arrivaltimes;

export const getArrivalTimeList = createSelector(getArrivalState, (arrivalT: any) => {
	return arrivalT.arrivalTimesInv;
});

export const getSelectedArrivalTime = createSelector(getArrivalState, (arrivalTime: any) => {
	return arrivalTime.selectedArrivalTime;
});

export const getselectedArrivalTimesForAllGuestsByBooking = createSelector(getArrivalState, (arrivalTime: any) => {
	return arrivalTime.selectedArrivalTimesForAllGuestsByBooking;
});

export const getselectedArrivalTimesForAllGuests = createSelector(getArrivalState, (arrivalTime: any) => {
	const listByBooking = arrivalTime.selectedArrivalTimesForAllGuestsByBooking;
	const guestArrivalTime : any = [];
	listByBooking.forEach((element: any) => {
		element.guests.map((g: any) => guestArrivalTime.push(g));
	});
	return guestArrivalTime;
});

export const getSelectedArrivalTimeVM = createSelector(getArrivalState, (arrivalTime: any) => {
	return arrivalTime.selectedArrivalTimeVM;
});

export const getSetSailPassState = (state: AppState) => state.setsailpass;
export const getSetSailPass = createSelector(getSetSailPassState, fromSSPass.getSetSailPass);

export const getRouterState = function (state$: Observable<AppState>) {
	return state$.pipe(select(s => s.router));
};
export const getNonObservableRouterState = (state: AppState) => state.router;
export const getRouterPath = createSelector(getNonObservableRouterState, (path: any) => {
	return path;
});

export const getSessionBookingGuest = createSelector(getEntitySelectedGuest, getEntitySelectedOther, (guest: any, otherGuest: any) => {
	let currentguest = guest;
	if (otherGuest !== null) {
		currentguest = null; // otherGuest
	}
	return currentguest;
});

export const getSessionBookigns = createSelector(getEntitySGuests, (guestList: any) => {
	return guestList.map((item: any) => item.bookingId).filter((item: any, index: any, arry: any) => arry.indexOf(item) === index);
});

export const getIsSailingClosed = createSelector(getSessionVar, (reservationRetrievalResp: any) => {
	if (reservationRetrievalResp !== null) {
		return reservationRetrievalResp.sailingClosed;
	}
});

export const getRetrievalFlags = createSelector(getSessionVar, (reservationRetrievalResp: any) => {
	if (reservationRetrievalResp !== null) {
		return {
			sailingClosed: reservationRetrievalResp.sailingClosed
		};
	}
});

export const getFullCreditCard = createSelector(getSessionVar, (reservationRetrievalResp: any) => {
	return reservationRetrievalResp.fullCreditCard;
});

export const getRetrivalValidationMessages = createSelector(getSiteResourceBundle, (resourceBundle: any) => {
	if (!resourceBundle) {
		return null;
	}

	return {
		lastName: {
			maxlength: resourceBundle['validation.global.maximum.characters'],
			pattern: resourceBundle['validation.global.characters1'],
			required: resourceBundle['error.global.required.info']
		},
		bookingId: {
			maxlength: resourceBundle['validation.global.maximum.characters'],
			pattern: resourceBundle['validation.global.characters.numbers.only'],
			required: resourceBundle['error.global.required.info']
		},

		dateGroup: {
			required: resourceBundle['error.global.required.info'],
			cannotBeToday: resourceBundle['validation.personal.details.dob.after.today']
		},

		shipCode: {
			required: resourceBundle['error.global.required.info']
		},
		genericError: resourceBundle['error.global.incorrect.information']
	};
});

export const getSelectedGuestWithoutCtcConsent = createSelector(
	getSelectdGuestsForCheckinProcess,
	getGranteeConsentsInfo,
	(selectedGuests: any, granteesConsents: any) => {
		const guestWithCtc = granteesConsents.filter((g: any) => g.documentType === constants.CONSENTS_DOCUMENTS_TYPE.CTC);
		return guestWithCtc.length
			? selectedGuests.filter((g: any) => !guestWithCtc.find((gw: any) => g.guestId === gw.granteeInfo.guestId))
			: selectedGuests;
	}
);

export const getSavePayloadCTCInternational = createSelector(
	getCruiseContractState,
	getGranteeConsentsInfo,
	getSelectedGuestWithoutCtcConsent,
	(cruiseContract: any, consentList: any, selectedguestWithoutConsent: any) => {
		const grantorinfo = cruiseContract.grantorInfo;
		const granteeListToSave = selectedguestWithoutConsent.map((item: any) => {
			return {
				bookingId: item.bookingId,
				firstName: item.firstName,
				lastName: item.lastName,
				guestId: item.guestId
			};
		});

		const dataToSaveArray = [];
		for (const item of granteeListToSave) {
			const consent = {
				documentType: { noPaymentConsentDoc: constants.CONSENTS_DOCUMENTS_TYPE.CTC },
				granteeInfo: item,
				grantorInfo: grantorinfo
			};
			dataToSaveArray.push(consent);
		}

		return dataToSaveArray;
	}
);

// change the name of this selector

export const skipReservationRetrieval = createSelector(getContentRetrieval, (content: any) => {
	if (content !== null) {
		return content.skipReservationRetrieval;
	}
	return false;
});

/**
 * Cruise Survey Selectors
 */
export const getCruiseSurveyState = (state: AppState) => state.cruiseSurvey;
export const getPassengerCruiseSurveys = createSelector(getCruiseSurveyState, fromCruiseSurvey.getPassengerCruiseSurveys);

/**
 * get countries 
 */
export const getCountries = (state: AppState) => state.countries;